import styled from '@emotion/styled';
import {
  Button,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  notification,
  Select,
  Upload,
} from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaPlus, FaSearch, FaUpload } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BondEntity } from '../../../../entities/bond.entity';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { DetenteurEntity } from '../../../../entities/detenteur.entity';
import { ORANGE } from '../../../../shared/colors';
import { getYieldToMaturity } from '../../../dashboard/components/OrderEntry';
import { getBonds } from '../../../dashboard/network';
import { createFichier, emissionBond, getDetenteurs } from '../network';
import { ClientForm } from './ClientForm';

const Container = styled.div`
  .content {
    display: flex;
    margin-bottom: 15px;
    color: white;

    > div {
      flex: 1;
      margin-right: 20px;
    }

    .ant-form-item,
    .ant-form-item-label label {
      color: white;
    }
  }
`;

export const BondForm = () => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [detenteurs, setDetenteurs] = useState<DetenteurEntity[]>([]);
  const [bonds, setBonds] = useState<BondEntity[]>([]);
  const [bondCode, setBondCode] = useState('');
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  const history = useHistory();

  useEffect(() => {
    getDetenteurs(connectedUser.id).then(({ data }) => {
      setDetenteurs(data);
      setIsLoading(false);
    });
    getBonds().then(({ data }) => {
      setBonds(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchBond = () => {
    let found = bonds.find((bond) => bond.code === bondCode);
    if (found) {
      notification.info({ message: 'Un bond avec ce code exist' });
      form.setFieldsValue({
        code: found.code,
        pays: found.pays,
        instrument: found.instrument,
        dureeMaturite: found.dureeMaturite,
        dateEmission: moment(found.dateEmission),
        dateMaturite: moment(found.dateMaturite),
        tauxMarginal: Number(found.tauxMarginal),
        valeurNominale: 10000,
        frequencePaiement: found.frequencePaiement,
      });
    } else {
      notification.info({ message: 'No existing bonds' });
    }
  };

  return (
    <Container>
      <Form
        layout='vertical'
        style={{ listStyleType: 'none' }}
        form={form}
        labelCol={{ span: 20 }}
        wrapperCol={{ span: 25 }}
        initialValues={{ valeurNominale: 10000 }}
        onFinish={async (data) => {
          setIsLoading(true);

          const bondToCreate = {
            code: data.code,
            pays: data.pays,
            instrument: data.instrument,
            dureeMaturite: data.dureeMaturite,
            dateEmission: data.dateEmission.format('YYYY-MM-DD'),
            dateMaturite: data.dateMaturite.format('YYYY-MM-DD'),
            tauxMarginal: Number(data.tauxMarginal),
            valeurNominale: data.valeurNominale,
            frequencePaiement: data.frequencePaiement,
            createur: connectedUser.id,
            agence: connectedUser.agence.id,
          };

          const detailBondToCreate = {
            parentCode: data.code,
            subCode: data.code + '-1',
            qte: Number(data.quantite),
            montant: data.quantite * data.valeurNominale,
            prixNominale: (data.valeurNominale * (100 - data.decote)) / 100,
            dateAcquisition: moment().format('YYYY-MM-DD'),
            decote: Number(data.decote),
            etat: '',
            originCode: '',
            tauxInteret: Number(data.tauxMarginal),
            tauxRendement:
              Math.round(
                getYieldToMaturity(
                  new Date(),
                  new Date(data.dateMaturite.format('YYYY-MM-DD')),
                  new Date(data.dateEmission.format('YYYY-MM-DD')),
                  data.tauxMarginal as number,
                  data.valeurNominale,
                  (data.valeurNominale * (100 - data.decote)) / 100,
                ) * 100, // round to 2 decimal places
              ) / 100,
            frequence: data.frequencePaiement,
            couponCouruPayer: 0.0,
            couponPercevoir: 0.0,
            commissionTotal: 0.0,
            transaction: null,
            bond: null,
            detenteur: detenteurs.find((det) => det.id === data.detenteur)?.id,
          };

          const compteToCreate = {
            code_svt: connectedUser.partenaire.code_referent,
            code_client: detenteurs.find((det) => det.id === data.detenteur)
              ?.code_referent,
            code_agence: connectedUser.agence.code,
          };

          let schema = '';
          if (data.elementControle.includes('PV')) {
            schema = 'ETPVTR'; // PV Adjudication - titre à recevoir
            detailBondToCreate.etat = '00';
          } else {
            let codeClient = detenteurs.find(
              (det) => det.id === data.detenteur,
            )?.code_referent;
            if (connectedUser.partenaire.code_referent === codeClient) {
              schema = 'ETNLCP'; // notification de livraison compte propre
              detailBondToCreate.etat = '01';
            } else {
              schema = 'ETNLCT'; // notification de livraison compte tier
              detailBondToCreate.etat = '01';
            }
          }

          const dataToCreate = {
            schema,
            bond: bondToCreate,
            // detenteurBond: detenteurBondToCreate,
            detailBond: detailBondToCreate,
            compte: compteToCreate,
            userId: connectedUser.id,
          };

          await emissionBond(dataToCreate).then(
            ({ data: newData, statusCode }) => {
              if (statusCode?.toString()[0] === '2') {
                const piece_justif = {
                  nom: data.elementControle,
                  media: data.piece?.fileList[0]?.originFileObj,
                  justif_bond: newData.url,
                };
                createFichier(piece_justif).then(({ data, statusCode }) => {
                  if (statusCode?.toString()[0] === '2') {
                    notification.success({
                      message: 'Succès',
                      description: 'Le titre a été créé avec succès',
                    });
                    history.goBack();
                  } else {
                    notification.info({
                      message: 'Erreur',
                      description: data.message,
                    });
                  }
                  setIsLoading(false);
                });
              } else {
                notification.info({
                  message: 'Erreur',
                  description: newData.message,
                });
                setIsLoading(false);
              }
            },
          );
        }}
      >
        <div className='content'>
          <div>
            <div style={{ display: 'flex' }}>
              <Form.Item
                label='Code du titre'
                name='code'
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: 'Entrez le code du titre',
                  },
                ]}
                style={{ width: '100%', marginRight: 10, marginBottom: 5 }}
              >
                <Input
                  placeholder='Entrez le code du titre'
                  value={bondCode}
                  onChange={(e) => setBondCode(e.target.value)}
                />
              </Form.Item>
              <Button
                icon={<FaSearch />}
                style={{ marginTop: 30 }}
                onClick={() => searchBond()}
              />
            </div>

            <Form.Item
              label='Instrument'
              name='instrument'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: "Sélectionnez l'instrument",
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <Select placeholder="Sélectionnez l'instrument">
                <Select.Option value='OTA'>OTA</Select.Option>
                <Select.Option value='BTA'>BTA</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label='Pays émetteur'
              name='pays'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Sélectionnez le pays emetteur',
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <Select placeholder='Sélectionnez le pays emetteur'>
                <Select.Option value='Cameroun'>Cameroun</Select.Option>
                <Select.Option value='Tchad'>Tchad</Select.Option>
                <Select.Option value='Congo'>Congo</Select.Option>
                <Select.Option value='Gabon'>Gabon</Select.Option>
                <Select.Option value='Centrafrique'>Centrafrique</Select.Option>
                <Select.Option value='Guinée Eq.'>
                  Guinée Equatoriale
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Date d'emission du titre"
              name='dateEmission'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: "Entrez la date d'emission",
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <DatePicker
                placeholder="Entrez la date d'emission"
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              label='Date de maturité du titre'
              name='dateMaturite'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Entrez la date de maturité',
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <DatePicker
                placeholder='Entrez la date de maturité'
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  Durée de maturité <i>(mois)</i>
                </span>
              }
              name='dureeMaturite'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Entrez la durée de maturité du titre',
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <Input
                placeholder='Entrez la durée de maturité'
                type='number'
                min={0}
              />
            </Form.Item>
            <Form.Item
              label='Coupon'
              name='tauxMarginal'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Entrez le coupon du titre',
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <Input
                placeholder='Entrez le coupon du titre'
                type='number'
                min={0}
                step={0.01}
              />
            </Form.Item>
            <Form.Item
              label='Valeur nominale du titre'
              name='valeurNominale'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Entrez la valeur nominale du titre',
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <Input
                placeholder='Entrez la valeur nominale du titre'
                type='number'
                min={0}
              />
            </Form.Item>
            <Form.Item
              label='Fréquence de paiement'
              name='frequencePaiement'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Sélectionnez la fréquence de paiement du titre',
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <Select placeholder='Sélectionnez la fréquence de paiement'>
                <Select.Option value='Annuelle'>Annuelle</Select.Option>
                <Select.Option value='Semestrielle'>Semestrielle</Select.Option>
                <Select.Option value='Trimestrielle'>
                  Trimestrielle
                </Select.Option>
                <Select.Option value='Infine'>Infine</Select.Option>
              </Select>
            </Form.Item>
          </div>
          <div>
            <Form.Item
              label='Décote'
              name='decote'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Entrez la décote du titre',
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <Input
                placeholder='Entrez la décote du titre'
                type='number'
                min={0}
                step={0.01}
              />
            </Form.Item>
            <Form.Item
              label='Quantité acheté'
              name='quantite'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Entrez la quantité du titre',
                },
              ]}
              style={{ marginBottom: 5 }}
            >
              <Input
                placeholder='Entrez la quantité du titre'
                type='number'
                min={0}
              />
            </Form.Item>

            <h4 style={{ marginTop: 20 }}>
              Informations sur le client (détenteur) du nouveau titre
            </h4>
            <Divider />

            <div style={{ display: 'flex' }}>
              <Form.Item
                label='Client détenteur'
                name='detenteur'
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: 'Sélectionnez le détenteur du titre',
                  },
                ]}
                style={{ width: '100%', marginRight: 10 }}
              >
                <Select placeholder='Sélectionnez le détenteur'>
                  {detenteurs.map((det) => (
                    <Select.Option key={det.id} value={det.id}>
                      {`${det.code_referent} - ${det.nom} ${det.prenom || ''}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Button
                icon={<FaPlus />}
                style={{ marginTop: 30 }}
                onClick={() => setShowDrawer(true)}
              />
            </div>
            <Form.Item
              label='Elément de contrôle'
              name='elementControle'
              hasFeedback
              rules={[
                {
                  required: true,
                  message: 'Sélectionnez un élément de contrôle',
                },
              ]}
            >
              <Select placeholder='Sélectionnez un élément de contrôle'>
                <Select.Option value='PV Adjudication Marché Monétaire'>
                  PV Adjudication Marché Monétaire
                </Select.Option>
                <Select.Option value='Notification de livraison de titres de la CRCT'>
                  Notification de livraison de titres de la CRCT
                </Select.Option>
                {/* <Select.Option value='Notification de remboursement de titres reçue de la CRCT'>
                  Notification de remboursement de titres reçue de la CRCT
                </Select.Option>
                <Select.Option value='Notification de transfert de titres reçue de la CRCT'>
                  Notification de transfert de titres reçue de la CRCT
                </Select.Option> */}
              </Select>
            </Form.Item>
            <Form.Item label='Pièces justificatives' name='piece'>
              <Upload name='piece'>
                <Button icon={<FaUpload style={{ marginRight: 10 }} />}>
                  Sélectionnez le fichier
                </Button>
              </Upload>
            </Form.Item>
          </div>
        </div>

        <Form.Item>
          <Button
            type='primary'
            htmlType='submit'
            // size='large'
            loading={isLoading}
            style={{
              width: '50%',
              backgroundColor: ORANGE,
              borderWidth: 0,
              borderRadius: 5,
              marginBottom: 20,
            }}
          >
            Enregistrer
          </Button>
        </Form.Item>
      </Form>

      <Drawer
        title='Ajouter un nouveau client'
        placement='right'
        width={400}
        onClose={() => setShowDrawer(false)}
        open={showDrawer}
        closeIcon={<MdClose size={20} />}
      >
        <ClientForm
          setShowDrawer={setShowDrawer}
          getNewClient={(cli) => setDetenteurs([...detenteurs, cli])}
        />
      </Drawer>
    </Container>
  );
};
